import React, { useMemo, useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Alert, Button } from '@themesberg/react-bootstrap';

const baseStyle = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: '20px',
  borderWidth: 2,
  borderRadius: 2,
  borderColor: 'gray',
  borderStyle: 'dashed',
  backgroundColor: '#fafafa',
  color: '#bdbdbd',
  outline: 'none',
  transition: 'border .24s ease-in-out',
};

const activeStyle = {
  borderColor: '#2196f3',
};

const acceptStyle = {
  borderColor: '#00e676',
};

const rejectStyle = {
  borderColor: '#ff1744',
};

function Attachment({ acceptedFiles = [] }) {
  const [errorMessage, setErrorMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const validateFile = useCallback((file) => {
    // File size validation (1MB = 1048576 bytes)
    if (file.size > 1048576) {
      return 'File size must not exceed 1 MB';
    }
    
    // File type validation
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      return 'Only PDF, JPEG, and PNG files are accepted';
    }
    
    return null;
  }, []);

  const onDrop = useCallback((files) => {
    const file = files[0];
    const validationError = validateFile(file);
    
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
    
    setErrorMessage('');
    acceptedFiles.push(file);
    
    // Simulate upload progress for better UX
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 10;
      });
    }, 100);
  }, [acceptedFiles, validateFile]);

  const removeFile = useCallback((index) => {
    acceptedFiles.splice(index, 1);
    setUploadProgress(0);
  }, [acceptedFiles]);

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    multiple: false,
    maxSize: 1048576, // 1MB
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept]
  );

  const files = acceptedFiles.map((file, index) => (
    <div key={file.path || index} className="d-flex justify-content-between align-items-center p-2 border rounded mb-2">
      <div>
        <strong>{file.path || file.name}</strong>
        <div className="text-muted small">
          {(file.size / 1024).toFixed(1)} KB
        </div>
      </div>
      <Button 
        variant="outline-danger" 
        size="sm" 
        onClick={() => removeFile(index)}
      >
        Remove
      </Button>
    </div>
  ));

  return (
    <div className="mv-2">
      <h4>Upload Attachment</h4>
      <div {...getRootProps({ style })}>
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Drop the file here...</p>
        ) : (
          <p>Drag 'n' drop a file here, or click to select a file</p>
        )}
      </div>
      
      <Alert variant="info" className="mt-2 mb-2">
        <small>
          <strong>Accepted formats:</strong> PDF, JPEG, PNG<br/>
          <strong>Maximum size:</strong> 1 MB
        </small>
      </Alert>
      
      {errorMessage && (
        <Alert variant="danger" className="mt-2">
          {errorMessage}
        </Alert>
      )}
      
      {uploadProgress > 0 && uploadProgress < 100 && (
        <div className="mt-2">
          <div className="progress">
            <div 
              className="progress-bar" 
              role="progressbar" 
              style={{ width: `${uploadProgress}%` }}
            >
              {uploadProgress}%
            </div>
          </div>
        </div>
      )}
      
      {files.length > 0 && (
        <div className="mt-3">
          <h6>Uploaded Files:</h6>
          {files}
        </div>
      )}
    </div>
  );
}

export default Attachment;
